import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import { CartBadge } from '@/components/customer/CartBadge'
import { CartDrawer } from '@/components/customer/CartDrawer'
import MenuItemCard from '@/components/customer/MenuItemCard'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useOrder, useRemoveCartItem, useUpdateCartItem } from '@/queries/useCart'
import { MenuItem, usePublicMenuItems } from '@/queries/useMenuItems'
import { useCartStore } from '@/store/cartStore'
import { useSessionStore } from '@/store/sessionStore'
import { AlertCircle, ShoppingBag } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

export default function MenuPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('customer')
  const [isCartOpen, setIsCartOpen] = useState(false)
  
  // Check session
  const { branchId, orderId, isSessionValid, clearSession } = useSessionStore()
  const { clearCart, setOrder, isCartEmpty } = useCartStore()
  
  // Cart mutations for backend sync
  const updateCartItemMutation = useUpdateCartItem()
  const removeCartItemMutation = useRemoveCartItem()
  
  // Fetch order to sync cart with backend
  const {
    data: orderData,
    isLoading: isLoadingOrder,
    error: orderError,
  } = useOrder(orderId)
  
  // Fetch menu items
  const {
    data: menuItems = [],
    isLoading,
    error,
    refetch,
  } = usePublicMenuItems(branchId)

  // Verify session exists before rendering
  useEffect(() => {
    if (!isSessionValid()) {
      navigate('/voice', { replace: true })
    }
  }, [isSessionValid, navigate])

  // Sync cart with order from backend on page load
  useEffect(() => {
    if (orderData && orderId) {
      // If order has items but cart is empty, restore cart from backend
      if (orderData.items && orderData.items.length > 0 && isCartEmpty()) {
        setOrder({
          orderId: orderData.orderId,
          items: orderData.items,
          total: orderData.total,
        })
      } else if (orderData.items && orderData.items.length > 0) {
        // If order has items and cart has items, sync to ensure consistency
        // (Backend is source of truth)
        setOrder({
          orderId: orderData.orderId,
          items: orderData.items,
          total: orderData.total,
        })
      }
    }
  }, [orderData, orderId, isCartEmpty, setOrder])

  // Monitor session expiration during order operations
  useEffect(() => {
    // Check session every 10 seconds
    const interval = setInterval(() => {
      if (!isSessionValid()) {
        // Session expired - clear cart and redirect
        clearCart()
        clearSession()
        navigate('/voice', { replace: true })
      }
    }, 10000)

    return () => clearInterval(interval)
  }, [isSessionValid, navigate, clearCart, clearSession])

  if (!isSessionValid()) {
    return null
  }

  // Show loading if fetching order or menu items
  const isLoadingData = isLoading || isLoadingOrder

  // Loading state
  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-4">
          {/* Header skeleton with Language Switcher */}
          <div className="flex justify-between items-start gap-4 mb-6">
            <div className="space-y-2 flex-1">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <div className="flex-shrink-0">
              <LanguageSwitcher />
            </div>
          </div>

          {/* Grid of skeleton cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-video w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-8 w-20" />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error || orderError) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error 
                ? t('menu.error.loadFailed', 'Unable to load menu. Please try again.')
                : t('cart.error.loadFailed', 'Unable to load cart. Please try again.')}
            </AlertDescription>
          </Alert>
          <Button onClick={() => {
            if (error) refetch()
            // Order will auto-refetch via TanStack Query
          }} variant="outline">
            {t('common:buttons.retry', 'Retry')}
          </Button>
        </div>
      </div>
    )
  }

  // Empty state
  if (!menuItems || menuItems.length === 0) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <ShoppingBag className="h-16 w-16 text-slate-300 mb-4" />
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">
              {t('menu.emptyState.title', 'No items available')}
            </h2>
            <p className="text-slate-600 mb-6">
              {t(
                'menu.emptyState.message',
                'No items available at this time. Please check back later or contact staff.'
              )}
            </p>
            <Button
              onClick={() => navigate('/voice')}
              variant="outline"
            >
              {t('common:buttons.goBack', 'Go Back')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Group items by category (first tag)
  const groupedItems = menuItems.reduce(
    (acc, item) => {
      const category = item.tags?.[0] || 'Other'
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(item)
      return acc
    },
    {} as Record<string, MenuItem[]>
  )

  const sortedCategories = Object.keys(groupedItems).sort()

  // Main content
  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header with Language Switcher */}
        <div className="flex justify-between items-start gap-4">
          <div className="space-y-2 flex-1">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
              {t('menu.title', 'Our Menu')}
            </h1>
            <p className="text-slate-600">
              {t('menu.subtitle', 'Select items to add to your order')}
            </p>
            <p className="text-sm text-slate-500">
              {menuItems.length} {t('menu.itemsAvailable', 'items available')}
            </p>
          </div>
          <div className="flex-shrink-0">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Menu items grouped by category */}
        {sortedCategories.map((category) => (
          <div key={category} className="space-y-4">
            {/* Category header */}
            <h2 className="text-2xl font-semibold text-slate-800 border-b-2 border-slate-200 pb-2">
              {category}
            </h2>

            {/* Category items grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {groupedItems[category].map((item) => (
                <MenuItemCard 
                  key={item.id} 
                  item={item}
                  onAddedToCart={() => setIsCartOpen(true)}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Cart Drawer */}
        <CartDrawer
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
          onUpdateQuantity={(itemId, quantity) => {
            updateCartItemMutation.mutate({ itemId, quantity })
          }}
          onRemoveItem={(itemId) => {
            removeCartItemMutation.mutate(itemId)
          }}
          isUpdating={updateCartItemMutation.isPending || removeCartItemMutation.isPending}
          error={
            updateCartItemMutation.error || removeCartItemMutation.error
              ? (updateCartItemMutation.error || removeCartItemMutation.error)?.message || t('cart.error.operationFailed', 'Operation failed. Please try again.')
              : null
          }
          onRetry={() => {
            if (updateCartItemMutation.error) {
              updateCartItemMutation.reset()
            }
            if (removeCartItemMutation.error) {
              removeCartItemMutation.reset()
            }
          }}
        />

        {/* Cart Badge (Fixed Position) */}
        <CartBadge onClick={() => setIsCartOpen(true)} />
      </div>
    </div>
  )
}
